/**
 * Recherche **hors ligne**, sur la réplique déjà en cache
 * (`ARCHITECTURE_V2.md` §3.1, étape 4 du §8).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * CE QUE CE FICHIER RÉSOUT
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Le §3.1 promet trois choses sans réseau : « rechercher dans la base
 * personnelle, les recettes, les exercices », et « scanner un produit déjà
 * présent dans la base personnelle ». Aucune des trois ne marchait, et pour une
 * raison unique et purement mécanique : chaque écran interrogeait le serveur
 * **avec le terme dans la clé de cache** (`['foods', 'poul']`). Hors ligne, un
 * terme jamais tapé auparavant n'a rien à restaurer, la requête reste en pause
 * (`networkMode: 'online'`, voulu pour les lectures), et l'écran attend
 * indéfiniment.
 *
 * 🔴 **La correction n'est pas « mettre la recherche en cache », c'est
 * déplacer le filtre.** On réplique la collection **entière** sous une clé
 * unique et stable, persistée par `lib/persist.ts`, et on filtre en mémoire.
 * Le §3.1 le dit d'ailleurs dans ces termes : « la base personnelle est
 * répliquée intégralement côté client, et c'est ce qui rend le scan hors ligne
 * possible ».
 *
 * ⚠️ **Le volume a été vérifié, pas supposé** — c'est ce qui autorise la
 * méthode :
 *
 *   | Collection | Lignes      | Poids            |
 *   |------------|-------------|------------------|
 *   | aliments   | centaines   | < 100 Ko (§3.1)  |
 *   | recettes   | dizaines    | négligeable      |
 *   | exercices  | **873**     | ≈ 300 Ko sans les instructions (571 Ko à elles seules, laissées au détail) |
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 UN SEUL CHEMIN, PAS DEUX
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Le filtrage est local **toujours**, pas « quand le réseau manque ». Deux
 * chemins auraient produit exactement le défaut que le projet traque depuis le
 * banc de l'étape 2 : un comportement **intermittent**, qui marche à la maison
 * et pas en salle, et dont on ne sait jamais s'il vient du réseau ou de soi.
 * Ici, hors ligne, il ne se passe simplement *rien de différent*.
 *
 * Le classement, lui, vient de `shared/search.ts` — le **même** module que les
 * routes du serveur. Sans ça, la même saisie renverrait les mêmes résultats
 * dans un autre ordre selon la connexion, et personne ne signalerait jamais ce
 * défaut : on se dirait qu'on a mal tapé.
 *
 * ⚠️ **Open Food Facts reste hors de portée**, et c'est le contrat du §3.1 : la
 * moitié personnelle du résultat s'affiche normalement, la moitié OFF annonce
 * son indisponibilité (`offStatus: 'unavailable'`, mécanisme de l'étape 6).
 */

import type { ExerciseSummaryDTO, FoodDTO, RecipeDTO, RoutineDTO } from '../../../shared/types';
import {
  compareByFavoriteThenName,
  compareScored,
  foldText,
  scoreAnySecondary,
  scorePrimary,
} from '../../../shared/search';

/* ==========================================================================
 * Aliments
 * ======================================================================= */

/**
 * ⚠️ **Le même score que `scoreFood()` dans `server/src/routes/foods.ts`** :
 * nom, puis marque, puis code-barres. Les deux lisent les mêmes primitives ;
 * ce qui reste dupliqué, c'est l'ordre des champs — c'est-à-dire la seule
 * chose qui soit propre au domaine, et la seule que le serveur ne puisse pas
 * décider pour le client.
 */
export function searchFoodsLocally(foods: readonly FoodDTO[], term: string): FoodDTO[] {
  const needle = foldText(term);
  if (needle === '') return [...foods].sort(compareByFavoriteThenName);

  const scored: { score: number; isFavorite: boolean; name: string; row: FoodDTO }[] = [];
  for (const row of foods) {
    const principal = scorePrimary(row.name, needle);
    const secondaire = principal === null ? scoreAnySecondary([row.brand], needle) : null;
    // Le code-barres est comparé brut : il n'a ni casse ni accent.
    const parCode =
      principal === null && secondaire === null && row.barcode !== null && row.barcode.includes(needle)
        ? 2
        : null;
    const score = principal ?? secondaire ?? parCode;
    if (score !== null) scored.push({ score, isFavorite: row.isFavorite, name: row.name, row });
  }
  return scored.sort(compareScored).map((item) => item.row);
}

/**
 * Le produit de la base personnelle portant ce code-barres, ou `null`.
 *
 * 🔴 **C'est le scan hors ligne du §3.1**, et c'est aussi ce qui rend un
 * rescan instantané **avec** réseau. Le serveur applique déjà la priorité du
 * §7.2 — « quand une fiche personnelle porte ce code, OFF n'est pas appelé du
 * tout » — mais il faut encore l'atteindre. Poser la même question à la
 * réplique supprime l'aller-retour au lieu de l'accélérer.
 *
 * ⚠️ **`barcode` ET `offCode`**, comme la déduplication du serveur : une fiche
 * importée d'OFF garde le code dans `offCode` et n'a pas toujours recopié le
 * `barcode`. N'interroger que le premier ferait repartir vers OFF un produit
 * pourtant déjà en base — c'est-à-dire perdre la propriété qu'on cherche.
 *
 * ⚠️ **La plus ancienne l'emporte** en cas de doublon (`id` le plus petit),
 * comme `orderBy(asc(food.id))` côté serveur. Un ordre arbitraire donnerait un
 * résultat qui change d'un scan à l'autre.
 */
export function findFoodByBarcode(foods: readonly FoodDTO[], code: string): FoodDTO | null {
  let trouve: FoodDTO | null = null;
  for (const row of foods) {
    if (row.barcode !== code && row.offCode !== code) continue;
    if (trouve === null || row.id < trouve.id) trouve = row;
  }
  return trouve;
}

/* ==========================================================================
 * Recettes
 * ======================================================================= */

/**
 * ⚠️ **Nom et tags**, comme `searchRecipes()` côté serveur (étape 7). Le filtre
 * par tag est séparé de la recherche textuelle, et il est **exact** — c'est ce
 * que fait le serveur, et c'est ce qu'on attend d'une puce de filtre qu'on a
 * touchée : « batch » ne doit pas ramener « batch cooking ».
 */
export function searchRecipesLocally(
  recipes: readonly RecipeDTO[],
  term: string,
  tag?: string | null,
): RecipeDTO[] {
  let liste = [...recipes];

  if (tag != null && tag !== '') {
    const cible = foldText(tag);
    liste = liste.filter((row) => row.tags.some((item) => foldText(item) === cible));
  }

  const needle = foldText(term);
  if (needle === '') return liste.sort(compareByFavoriteThenName);

  const scored: { score: number; isFavorite: boolean; name: string; row: RecipeDTO }[] = [];
  for (const row of liste) {
    const score = scorePrimary(row.name, needle) ?? scoreAnySecondary(row.tags, needle);
    if (score !== null) scored.push({ score, isFavorite: row.isFavorite, name: row.name, row });
  }
  return scored.sort(compareScored).map((item) => item.row);
}

/* ==========================================================================
 * Exercices
 * ======================================================================= */

/** Filtres de l'écran Exercices — tous facultatifs, tous cumulatifs. */
export interface ExerciseFilters {
  term?: string;
  category?: string | null;
  equipment?: string | null;
  muscle?: string | null;
  favoriteOnly?: boolean;
}

/**
 * ⚠️ **Le nom d'abord, puis muscles / matériel / catégorie.** Un seul rang pour
 * tous les champs secondaires (`scoreAnySecondary`) : les hiérarchiser entre
 * eux demanderait de justifier chaque rang, et ce que l'utilisateur perçoit,
 * c'est seulement que les correspondances de nom sont en tête.
 *
 * ⚠️ **Les muscles entrent dans la recherche textuelle**, pas seulement dans le
 * filtre : taper « biceps » doit trouver les exercices de biceps, même quand
 * aucun ne s'appelle ainsi. C'est l'extension déjà consentie aux tags de
 * recettes à l'étape 7 — sans elle, la moitié du vocabulaire du jeu importé
 * serait inatteignable.
 */
export function searchExercisesLocally(
  exercises: readonly ExerciseSummaryDTO[],
  filters: ExerciseFilters,
): ExerciseSummaryDTO[] {
  let liste = [...exercises];

  if (filters.favoriteOnly === true) liste = liste.filter((row) => row.isFavorite);
  if (filters.category != null && filters.category !== '') {
    liste = liste.filter((row) => row.category === filters.category);
  }
  if (filters.equipment != null && filters.equipment !== '') {
    liste = liste.filter((row) => row.equipment === filters.equipment);
  }
  if (filters.muscle != null && filters.muscle !== '') {
    const cible = filters.muscle;
    liste = liste.filter(
      (row) => row.primaryMuscles.includes(cible) || row.secondaryMuscles.includes(cible),
    );
  }

  const needle = foldText(filters.term ?? '');
  if (needle === '') return liste.sort(compareByFavoriteThenName);

  const scored: { score: number; isFavorite: boolean; name: string; row: ExerciseSummaryDTO }[] = [];
  for (const row of liste) {
    const score =
      scorePrimary(row.name, needle) ??
      scoreAnySecondary(
        [...row.primaryMuscles, ...row.secondaryMuscles, row.equipment, row.category],
        needle,
      );
    if (score !== null) scored.push({ score, isFavorite: row.isFavorite, name: row.name, row });
  }
  return scored.sort(compareScored).map((item) => item.row);
}

/* ==========================================================================
 * Routines (V2, étape 5)
 * ======================================================================= */

/**
 * ⚠️ **Nom d'abord, puis les noms des exercices employés.** Un seul rang pour
 * tous les champs secondaires (`scoreAnySecondary`), comme partout ailleurs.
 *
 * ⚠️ **Les exercices entrent dans la recherche**, et c'est l'extension déjà
 * consentie aux muscles d'un exercice à l'étape 4 et aux tags d'une recette à
 * l'étape 7 : on cherche « squat » pour retrouver la séance qui en contient, et
 * personne ne nomme ses routines d'après leur contenu.
 *
 * ⚠️ **Aucun appel réseau, aucune requête par terme** — même mécanique que les
 * trois autres domaines : la réplique est restaurée par `lib/persist.ts` et le
 * filtre ne demande rien à personne. C'est ce qui fait qu'en salle, chercher
 * une routine se comporte exactement comme à la maison.
 */
export function searchRoutinesLocally(
  routines: readonly RoutineDTO[],
  term: string,
  favoriteOnly = false,
): RoutineDTO[] {
  let liste = [...routines];
  if (favoriteOnly) liste = liste.filter((row) => row.isFavorite);

  const needle = foldText(term);
  if (needle === '') return liste.sort(compareByFavoriteThenName);

  const scored: { score: number; isFavorite: boolean; name: string; row: RoutineDTO }[] = [];
  for (const row of liste) {
    const exercices = row.blocks.flatMap((block) => block.lines.map((line) => line.exerciseName));
    const score = scorePrimary(row.name, needle) ?? scoreAnySecondary(exercices, needle);
    if (score !== null) scored.push({ score, isFavorite: row.isFavorite, name: row.name, row });
  }
  return scored.sort(compareScored).map((item) => item.row);
}

/**
 * Ce qu'une routine pèse, pour la ligne de résumé de la liste.
 *
 * ⚠️ **Dérivé de la réplique, jamais demandé au serveur** — exactement le
 * raisonnement des facettes d'exercice : deux propriétés gratuites, ça marche
 * sans réseau et ça ne peut pas se désaccorder de l'arbre dont ça sort.
 *
 * ⚠️ **`sets` compte les séries planifiées, tours de circuit compris** : un
 * circuit de 3 tours sur 4 lignes à 1 série, c'est 12 séries à faire, pas 4.
 * C'est le §4.3 pris au mot — sans quoi le résumé mentirait sur la durée.
 */
export function routineWeight(routine: RoutineDTO): {
  blocks: number;
  exercises: number;
  sets: number;
} {
  let exercises = 0;
  let sets = 0;
  for (const block of routine.blocks) {
    exercises += block.lines.length;
    const tours = block.kind === 'circuit' ? (block.rounds ?? 1) : 1;
    for (const line of block.lines) sets += line.sets * tours;
  }
  return { blocks: routine.blocks.length, exercises, sets };
}

/* ==========================================================================
 * Facettes
 * ======================================================================= */

/**
 * Vocabulaire réellement employé par la base, pour peupler les filtres.
 *
 * 🔴 **Dérivé de la réplique, pas demandé au serveur** — et c'est ce qui fait
 * qu'il n'y a pas de `GET /api/exercises/facets`. Deux propriétés qu'une route
 * n'aurait pas données : les filtres fonctionnent **sans réseau**, et ils ne
 * peuvent pas se désaccorder de la liste qu'ils filtrent, puisqu'ils en
 * sortent. Le contre-exemple est `GET /api/recipes/tags` (étape 7), qui est une
 * route parce qu'à l'époque le client n'avait pas la liste complète.
 *
 * ⚠️ Trié en français (`localeCompare`), jamais par `<` : la comparaison brute
 * range toutes les majuscules avant toutes les minuscules.
 */
export function exerciseFacets(exercises: readonly ExerciseSummaryDTO[]): {
  categories: string[];
  equipment: string[];
  muscles: string[];
} {
  const categories = new Set<string>();
  const equipment = new Set<string>();
  const muscles = new Set<string>();

  for (const row of exercises) {
    categories.add(row.category);
    if (row.equipment !== null && row.equipment !== '') equipment.add(row.equipment);
    for (const muscle of row.primaryMuscles) muscles.add(muscle);
    for (const muscle of row.secondaryMuscles) muscles.add(muscle);
  }

  const trier = (set: Set<string>): string[] =>
    [...set].sort((a, b) => a.localeCompare(b, 'fr'));

  return { categories: trier(categories), equipment: trier(equipment), muscles: trier(muscles) };
}
